import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaWhatsapp, FaPhone, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { LuPiano } from "react-icons/lu";
import { Link } from 'react-router-dom';
import SeoHead from '../components/Menu/components/SeoHead';
import '../assets/css/pianopage.css';

const PianoPage = () => {
  const { id } = useParams();
  const [piano, setPiano] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    const fetchPiano = async () => {
      try {
        const response = await fetch(`https://pianoswaldir.com.br/api/pianos.php?action=view&id=${id}`);
        const data = await response.json();
        setPiano(data);
        setLoading(false);
      } catch (error) {
        console.error('Erro ao carregar piano:', error);
        setLoading(false);
      }
    };

    fetchPiano();
  }, [id]);

  if (loading) {
    return (
      <div className="loading-container">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
          className="loading-spinner"
        >
          <LuPiano size={32} />
        </motion.div>
        <p>Carregando detalhes do piano...</p>
      </div>
    );
  }

  if (!piano) {
    return (
      <div className="not-found-container">
        <h2>Piano não encontrado</h2>
        <p>O piano que você está procurando não está disponível no momento.</p>
      </div>
    );
  }

  // Schema para produto individual
  const productSchema = SeoHead.generateProductSchema({
    ...piano,
    id: id
  });

  return (
    <div className="container">
  <SeoHead
  title={`${piano.marca} ${piano.modelo} | Waldir Pianos`}
  description={`Piano ${piano.marca} ${piano.modelo}. ${piano.descricao?.replace(/<[^>]*>/g, '').substring(0, 160) || ''} Compre ou saiba mais sobre este instrumento único na Waldir Pianos.`}
  keywords={`${piano.marca}, ${piano.modelo}, piano ${piano.marca} ${piano.modelo}, piano usado, piano restaurado, comprar piano, piano à venda, waldir pianos`} 
  image={piano.foto_principal}
  url={`https://pianoswaldir.com.br/pianos/${id}`}
  schemaMarkup={productSchema}
  type="product"
/>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="piano-page-container"
      >
        <div className="piano-breadcrumb">
          <Link to="/pianos" className="back-link">
            <FaChevronLeft /> Voltar para todos os pianos
          </Link>
        </div>

        <div className="piano-details">
          <motion.div 
            className="piano-gallery"
            initial={{ x: -20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <div className="main-image-container">
              <img
                src={
                  currentImageIndex === 0 
                    ? piano.foto_principal 
                    : piano.galeria[currentImageIndex - 1]?.foto || piano.galeria[currentImageIndex - 1]
                }
                alt={`${piano.marca} ${piano.modelo}`}
                className="main-image-venda"
                itemProp="image"
              />
              {piano.galeria && piano.galeria.length > 0 && (
                <>
                  <button className="nav-button prev" onClick={() => setCurrentImageIndex(prev => prev === 0 ? piano.galeria.length - 1 : prev - 1)}>
                    <FaChevronLeft />
                  </button>
                  <button className="nav-button next" onClick={() => setCurrentImageIndex(prev => prev === piano.galeria.length - 1 ? 0 : prev + 1)}>
                    <FaChevronRight />
                  </button>
                </>
              )}
            </div>

            {piano.galeria && piano.galeria.length > 0 && (
              <div className="thumbnails">
                {[piano.foto_principal, ...piano.galeria.map(item => item.foto || item)].map((img, index) => (
                  <motion.div
                    key={index}
                    className={`thumbnail-container ${index === currentImageIndex ? 'active' : ''}`}
                    whileHover={{ scale: 1.05 }}
                    onClick={() => setCurrentImageIndex(index)}
                  >
                    <img src={img} alt={`Vista ${index + 1} do piano ${piano.marca} ${piano.modelo}`} className="thumbnail" />
                  </motion.div>
                ))}
              </div>
            )}
          </motion.div>

          <motion.div
            className="piano-info"
            initial={{ x: 20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            itemScope
            itemType="https://schema.org/Product"
          >
            <h1 itemProp="name">{piano.marca} {piano.modelo}</h1>
            
            <div className="piano-meta">
              <span className="price" itemProp="offers" itemScope itemType="https://schema.org/Offer">
                <span itemProp="priceCurrency" content="BRL"></span>
                <span itemProp="price" content={piano.valor}>
                  {new Intl.NumberFormat('pt-BR', {
                    style: 'currency',
                    currency: 'BRL'
                  }).format(piano.valor)}
                </span>
              </span>
              <span className={`status ${piano.status}`}>
                {piano.status === 'disponivel' ? 'Disponível' : 
                 piano.status === 'vendido' ? 'Vendido' : 'Em Breve'}
              </span>
            </div>

            <div className="description" itemProp="description" dangerouslySetInnerHTML={{ __html: piano.descricao }} />

            <div className="piano-actions">
              <motion.a
                href={`https://wa.me/+5511969953700?text=Interessado%20no%20piano%20${encodeURIComponent(piano.marca)}%20${encodeURIComponent(piano.modelo)}%20(${encodeURIComponent(id)})`}
                className="whatsapp-button"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <FaWhatsapp /> Comprar
              </motion.a>
              <motion.a
                href="tel:1122565987"
                className="phone-button"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <FaPhone /> Fale Conosco
              </motion.a>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
};

export default PianoPage;